import { useEffect, useRef, useState } from "react";
import { CorrectionBillEditBillInfoProps } from "../types/CorrectionBillEdit.types";
import { PrintData } from "../../../DirectBilling/Edit/exports";
import {
  ProcessingCode,
  TestType,
  TestTypeInterface,
} from "../../../DirectBilling/Edit/types/TestTypes.interface";
import useStore from "../../../../../../helpers/useStore";
import { TransactionsContext } from "../../../../../../stores/Transactions/Transactions.provider";
import { RxStoreContext } from "../../../../../../stores/Rx/rx.provider";
import notificationStore from "../../../../../../stores/Notification/notificationStore";
import { useReactToPrint } from "react-to-print";
import {
  UpdateInsuranceTransactionDto,
  BillPdfBatchItemDtoPdfTypeEnum,
} from "../../../../../../api-client";
import { useBatchPrintPdf } from "../../../../../../hooks/useBatchPrintPdf";
import { extractDocIdsFromBatchResults } from "../../../../../../utils/billing/documentDownload";

export const useCorrectionBillEditBillInfo = ({
  bills,
  setBills,
  onRowClick,
  onRxDelete,
  onRemoveAll,
  onValidationFilter,
  handleFullScreenDialogClose,
  handleDruckenOverride,
}: CorrectionBillEditBillInfoProps) => {
  // Local states
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRx, setSelectedRx] = useState<any | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [isSoftDeleteDialogOpen, setIsSoftDeleteDialogOpen] = useState(false);
  const [rxNumber, setRxNumberInput] = useState(""); // For adding single RX (input value)
  const [rxNumberError, setRxNumberError] = useState("");
  const [openSingleRxDialog, setOpenSingleRxDialog] = useState(false);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [openExportDialog, setOpenExportDialog] = useState(false);
  const [selectedRxId, setSelectedRxId] = useState<string | null>(null);
  const [openTaxierenDialog, setOpenCancelTaxierenDialog] = useState(false);

  const [printData, setPrintData] = useState<PrintData | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isDrucken, setIsDrucken] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);
  const [testToggleChecked, setTestToggleChecked] = useState(false);
  const [openTestTypeDialog, setOpenTestTypeDialog] = useState(false);
  const [testTypeData, setTestTypeData] = useState<TestTypeInterface>({
    type: TestType.GLOBAL,
    email: undefined,
  });
  // State for loading spinner
  const [isExportLoading, setIsExportLoading] = useState(false);
  const today = new Date();
  const todayString = today.toDateString();

  // State for Stepper
  const [billsChanged, setBillsChanged] = useState(false);
  const [billsSnapshot, setBillsSnapshot] = useState(JSON.stringify(bills));

  // If bills changes at any time, detect it
  useEffect(() => {
    const newSnapshot = JSON.stringify(bills);
    // If the snapshot differs from the last "saved" snapshot, mark as changed
    if (newSnapshot !== billsSnapshot) {
      setBillsChanged(true);
    }
  }, [bills, billsSnapshot]);

  // API wrappers from stores
  const TransactionStore = useStore(TransactionsContext);
  const {
    submitEditedBillingData,
    exportData,
    softDeleteTransaction,
    mergeDirectBillingDocuments,
  } = TransactionStore;

  const RxStore = useStore(RxStoreContext);
  const { updateIsTaxieren } = RxStore;

  // Batch print hook for CORRECTION bills
  const {
    startBatchPrint,
    isLoading: isBatchPrinting,
    progress: batchProgress,
  } = useBatchPrintPdf(BillPdfBatchItemDtoPdfTypeEnum.Correction);

  // Initialize notes with data from bills
  useEffect(() => {
    const initialNotes = bills.reduce(
      (acc, bill) => {
        acc[bill.billId] = bill.notes || "";
        return acc;
      },
      {} as Record<string, string>,
    );
    setNotes(initialNotes);
  }, [bills]);

  // ----------------
  // Menu / Kebab
  // ----------------
  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, rx: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedRx(rx);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // ----------------
  // Delete RX
  // ----------------

  const handleDeleteClick = () => {
    if (
      bills.length === 1 &&
      bills[0].data.rxs.length === 1 &&
      bills[0].data.rxs.some((rx) => rx.id === selectedRx?.id)
    ) {
      setIsSoftDeleteDialogOpen(true); // Open soft delete dialog
    } else {
      setOpenDeleteDialog(true); // Open normal delete dialog
    }
    handleMenuClose();
  };

  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
    setSelectedRx(null);
  };

  const handleDeleteConfirm = async () => {
    try {
      if (!selectedRx) return;

      let billToPersist: any = null;

      /* ---------- UI update ---------- */
      setBills((prevBills) =>
        prevBills
          .map((bill) => {
            if (bill.data.rxs.some((rx) => rx.id === selectedRx.id)) {
              const updatedRxData = bill.data.rxs.filter((rx) => rx.id !== selectedRx.id);
              const updatedTotalPrice = updatedRxData.reduce(
                (total, rx) => total + (rx.rxPrice.netValue || 0),
                0,
              );

              billToPersist = {
                ...bill,
                data: {
                  ...bill.data,
                  rxs: updatedRxData,
                  totalPrice: updatedTotalPrice,
                },
              };
              return billToPersist;
            }
            return bill;
          })
          .filter((bill) => bill.data.rxs.length > 0),
      );

      /* ---------- backend save ---------- */
      if (billToPersist) {
        const { payerIK, accountInfo, data, billId, userInfo, wasExported } = billToPersist;
        const { rxs, payerDto, totalPrice } = data;

        const rxsIds = rxs.map((rx: any) => rx.id).filter((id: any): id is string => !!id);
        const paperCenter = payerDto?.paperReceivingCenter?.ik;
        const digitalCenter = payerDto?.digitalReceivingCenter?.ik;

        const dataSubmission: UpdateInsuranceTransactionDto = {
          payerId: payerIK,
          accountId: accountInfo.id,
          amount: totalPrice,
          userId: userInfo.id!,
          rxIds: rxsIds,
          paperCenterId: paperCenter || undefined,
          dataCenterId: digitalCenter || undefined,
          notes: notes[billId] || "",
          wasExported: wasExported,
          tAccountId: rxs[0].tarifGroupId === "Privat" ? 4105 : 4101,
        };

        await submitEditedBillingData(billId, dataSubmission);
        notificationStore.showMessage("Rezept erfolgreich aus der Abrechnung entfernt", "success");
      }

      onRxDelete(selectedRx.id);
      setSelectedRx(null);
      setOpenDeleteDialog(false);
    } catch (error) {
      console.error("Error deleting RX:", error);
      notificationStore.showMessage("Fehler beim Entfernen des Rezepts", "error");
    }
  };

  // ----------------
  // Soft Delete
  // ----------------

  const handleSoftDeleteCancel = () => {
    setIsSoftDeleteDialogOpen(false);
    setSelectedRx(null);
  };

  const handleSoftDeleteConfirm = async () => {
    if (!bills[0].billId) return;

    try {
      await softDeleteTransaction(bills[0].billId);
      setBills([]);
      notificationStore.showMessage("Erfolgreich gelöscht", "success");
    } catch (error) {
      console.error("Soft delete failed:", error);
      notificationStore.showMessage("Fehler beim Löschen", "error");
    }
    setSelectedRx(null);
    setIsSoftDeleteDialogOpen(false);
    handleFullScreenDialogClose(true);
  };

  // ----------------
  // Printing (Taxieren)
  // ----------------
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    onAfterPrint: async () => {
      if (!selectedRx) {
        setIsPrinting(false);
        return;
      }

      try {
        const response = await updateIsTaxieren(selectedRx.id, "true");
        if (!response || !response.rx) {
          throw new Error("No updated RX returned from server");
        }

        const updatedRx = response.rx;
        const updatedIsTaxieren = updatedRx.isTaxieren;

        // Update the bills state
        setBills((prevBills) =>
          prevBills.map((bill) => {
            if (bill.data.rxs.some((rx) => rx.id === selectedRx.id)) {
              return {
                ...bill,
                data: {
                  ...bill.data,
                  rxs: bill.data.rxs.map((rx) =>
                    rx.id === selectedRx.id ? { ...rx, isTaxieren: updatedIsTaxieren } : rx,
                  ),
                },
              };
            }
            return bill;
          }),
        );

        notificationStore.showMessage("Rezept wurde taxiert", "success");
      } catch (error) {
        console.error("Error updating isTaxieren after print:", error);
        notificationStore.showMessage("Fehler beim Taxieren des Rezepts", "error");
      }

      setIsPrinting(false);
      setPrintData(null);
      setSelectedRx(null);
    },
  });

  const handleTaxierenClick = (row: any) => {
    if (!row) return;

    setSelectedRx(row);

    const bill = bills.find((b) => b.data.rxs.some((rx) => rx.id === row.id));
    if (!bill) {
      notificationStore.showMessage("Fehler: Keine Rechnung für dieses Rezept gefunden", "error");
      return;
    }

    const careProviderIk = bill.accountInfo.ik || "";
    const billNumber = bill.billNumber;
    const rxNumber = row.rxNumber;
    const isZahnarzt = row.rxtype.id === "Zahnarzt" ? true : false;

    setPrintData({ billNumber, rxNumber, careProviderIk, isZahnarzt });
    setIsPrinting(true);
  };

  const handleCancelTaxierenClick = () => {
    setOpenCancelTaxierenDialog(true);
    handleMenuClose();
  };

  const handleCancelTaxierenConfirm = async () => {
    if (!selectedRx) return;

    try {
      const response = await updateIsTaxieren(selectedRx.id, "false");
      if (!response || !response.rx) {
        throw new Error("No updated RX returned from server");
      }

      const updatedRx = response.rx;
      const updatedIsTaxieren = updatedRx.isTaxieren;

      // Update the bills state
      setBills((prevBills) =>
        prevBills.map((bill) => {
          if (bill.data.rxs.some((rx) => rx.id === selectedRx.id)) {
            return {
              ...bill,
              data: {
                ...bill.data,
                rxs: bill.data.rxs.map((rx) =>
                  rx.id === selectedRx.id ? { ...rx, isTaxieren: updatedIsTaxieren } : rx,
                ),
              },
            };
          }
          return bill;
        }),
      );

      notificationStore.showMessage("Sie haben die Taxieren erfolgreich storniert", "success");
    } catch (error) {
      console.error("Error updating isTaxieren after print:", error);
      notificationStore.showMessage("Fehler beim Untaxieren des Rezepts", "error");
    }

    setOpenCancelTaxierenDialog(false);
  };

  useEffect(() => {
    if (printData && isPrinting) {
      handlePrint();
    }
  }, [printData, isPrinting, handlePrint]);

  // ----------------
  // Filter by Validation
  // ----------------
  const handleValidationFilter = () => {
    const updatedBills = bills
      .map((bill) => {
        const filteredRxs = bill.data.rxs.filter((rx) => {
          const validations = rx.formvalidation;
          return (
            validations &&
            Object.values(validations).every((validation) => validation.validity === "Valid")
          );
        });
        const updatedTotalPrice = filteredRxs.reduce(
          (total, rx) => total + (rx.rxPrice.netValue || 0),
          0,
        );

        return {
          ...bill,
          data: {
            ...bill.data,
            rxs: filteredRxs,
            totalPrice: updatedTotalPrice,
          },
        };
      })
      .filter((bill) => bill.data.rxs.length > 0);

    setBills(updatedBills);

    // notify parent
    const remainingRxs = updatedBills.flatMap((bill) => bill.data.rxs);
    onValidationFilter?.(remainingRxs);
  };

  // ----------------
  // Save All
  // ----------------
  const handleSave = async () => {
    try {
      const updatePromises = bills.map(async (bill) => {
        const { payerIK, accountInfo, data, billId, userInfo, wasExported } = bill;
        const { rxs, payerDto, totalPrice } = data;

        const rxsIds = rxs.map((rx) => rx.id).filter((id): id is string => !!id);
        const paperCenter = payerDto?.paperReceivingCenter?.ik;
        const digitalCenter = payerDto?.digitalReceivingCenter?.ik;

        const dataSubmission: UpdateInsuranceTransactionDto = {
          payerId: payerIK,
          accountId: accountInfo.id,
          amount: totalPrice,
          userId: userInfo.id!,
          rxIds: rxsIds,
          paperCenterId: paperCenter || undefined,
          dataCenterId: digitalCenter || undefined,
          notes: notes[bill.billId] || "",
          wasExported: wasExported,
          tAccountId: rxs[0].tarifGroupId === "Privat" ? 4105 : 4101,
        };

        await submitEditedBillingData(billId, dataSubmission);
      });

      await Promise.all(updatePromises);

      notificationStore.showMessage("Alle Transaktionen erfolgreich aktualisiert", "success");
      setBillsChanged(false);
      setBillsSnapshot(JSON.stringify(bills));
    } catch (error) {
      console.error("Fehler beim Aktualisieren der Transaktionen", error);
      notificationStore.showMessage("Fehler beim Aktualisieren der Transaktionen", "error");
    }
  };

  // ----------------
  // Bill Printing
  // ----------------

  const handleDrucken = async (testType: TestType) => {
    try {
      setIsDrucken(true);

      // Step 1: Pre-save all bills in parallel (update transaction data)
      const updatePromises = bills.map(async (bill) => {
        const { payerIK, accountInfo, data, billId, userInfo, wasExported } = bill;
        const { rxs, payerDto, totalPrice } = data;

        const rxsIds = rxs.map((rx) => rx.id).filter((id): id is string => !!id);
        const paperCenter = payerDto?.paperReceivingCenter?.ik;
        const digitalCenter = payerDto?.digitalReceivingCenter?.ik;

        const dataSubmission: UpdateInsuranceTransactionDto = {
          payerId: payerIK,
          accountId: accountInfo.id,
          amount: totalPrice,
          userId: userInfo.id!,
          rxIds: rxsIds,
          paperCenterId: paperCenter || undefined,
          dataCenterId: digitalCenter || undefined,
          notes: notes[bill.billId] || "",
          wasExported: wasExported,
          tAccountId: rxs[0].tarifGroupId === "Privat" ? 4105 : 4101,
        };

        // Pre-save transaction (update only, no PDF generation)
        await submitEditedBillingData(billId, dataSubmission);
      });

      await Promise.all(updatePromises);

      // Step 2: Queue batch PDF generation workflow
      const billIds = bills.map((bill) => bill.billId);
      const result = await startBatchPrint(billIds);
      const successfulResults = result?.results.filter((r) => r.success) ?? [];

      // Step 3: Extract doc IDs (no auto-download to match original behavior)
      if (successfulResults.length > 0) {
        const docIds = extractDocIdsFromBatchResults(successfulResults);
        // Merge PDFs without auto-download to preserve original behavior
        await mergeDirectBillingDocuments({
          paperDocumentIds: docIds.paperDocumentIds,
          billDocumentIds: docIds.billDocumentIds,
        });

        // Patch each bill's PDF links with the freshly generated document IDs
        // so the download buttons point at the new PDFs, not stale ones.
        setBills((prevBills) =>
          prevBills.map((bill) => {
            const jobResult = successfulResults.find((r) => r.entityId === bill.billId);
            const paperDocumentId = (jobResult as any)?.paperDocumentId;
            const billDocumentId = (jobResult as any)?.billDocumentId;
            if (!paperDocumentId && !billDocumentId) return bill;
            return {
              ...bill,
              ...(paperDocumentId ? { paperCenterReceipt: { id: paperDocumentId } as any } : {}),
              ...(billDocumentId ? { billPdf: { id: billDocumentId } as any } : {}),
            };
          }),
        );
      }

      // Show appropriate success/warning message
      if (result && result.failureCount > 0) {
        notificationStore.showMessage(
          `${result.successCount}/${result.totalCount} Korrektur-Rechnungen erfolgreich erstellt. ${result.failureCount} Fehler.`,
          "warning",
        );
      } else {
        notificationStore.showMessage(
          "Urbelegbegleitschreiben wurde erfolgreich als PDF erstellt.",
          "success",
        );
      }

      setBillsChanged(false);
      setBillsSnapshot(JSON.stringify(bills));
    } catch (error: any) {
      let errorMessage = error.response?.data?.message || error.message;
      console.error("Error Printing Bills", error);
      notificationStore.showMessage(
        `Beim Drucken der Rechnung ist ein Problem aufgetreten: ${errorMessage}`,
        "error",
        error,
      );
    } finally {
      setIsDrucken(false);
    }
  };

  // Use override if provided (e.g. for LineItem corrections), otherwise use default
  const effectiveHandleDrucken = handleDruckenOverride || handleDrucken;

  // ----------------
  // Export
  // ----------------
  const handleExport = () => {
    setOpenExportDialog(true);
  };

  const handleExportConfirm = async (testOptions: TestTypeInterface) => {
    try {
      setOpenExportDialog(false);
      setIsExportLoading(true);
      // console.log("testOptions too see if it works", testOptions);
      const updatePromises = bills.map(async (bill) => {
        const { payerIK, accountInfo, data, billId, userInfo, wasExported } = bill;
        const { rxs, payerDto, totalPrice } = data;

        const rxsIds = rxs.map((rx) => rx.id).filter((id): id is string => !!id);
        const paperCenter = payerDto?.paperReceivingCenter?.ik;
        const digitalCenter = payerDto?.digitalReceivingCenter?.ik;

        const dataSubmission: UpdateInsuranceTransactionDto = {
          payerId: payerIK,
          accountId: accountInfo.id,
          amount: totalPrice,
          userId: userInfo.id!,
          rxIds: rxsIds,
          paperCenterId: paperCenter || undefined,
          dataCenterId: digitalCenter || undefined,
          notes: notes[bill.billId] || "",
          wasExported: wasExported,
          tAccountId: rxs[0].tarifGroupId === "Privat" ? 4105 : 4101,
        };

        await submitEditedBillingData(billId, dataSubmission);
      });

      await Promise.all(updatePromises);

      // Then export
      await exportData(bills, testOptions, ProcessingCode.CORRECTION);

      setBills([]);
      handleFullScreenDialogClose(true);
    } catch (error) {
      console.error("Error exporting transactions", error);
      notificationStore.showMessage("Fehler beim Exportieren der Rechnungen", "error");
    } finally {
      setIsExportLoading(false);
    }
  };

  // Test toggle
  const handleTestToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setTestToggleChecked(isChecked);

    if (isChecked) {
      // If user just enabled test mode, open the dialog to let them pick
      setOpenTestTypeDialog(true);
    } else {
      // If user just turned test mode off, revert to default
      setTestTypeData({ type: TestType.GLOBAL, email: undefined });
    }
  };

  const handleTestTypeDialogClose = () => {
    setOpenTestTypeDialog(false);
  };

  // Handle notes change
  const handleNotesChange = (billId: string, noteValue: string) => {
    setNotes((prevNotes) => ({
      ...prevNotes,
      [billId]: noteValue,
    }));
  };

  // Return everything needed by the component
  return {
    // states
    anchorEl,
    selectedRx,
    openDeleteDialog,
    isSoftDeleteDialogOpen,
    bills,
    rxNumberInput: rxNumber,
    rxNumberError,
    openSingleRxDialog,
    notes,
    openExportDialog,
    selectedRxId,
    openTaxierenDialog,
    printData,
    isPrinting,
    isDrucken,
    batchProgress,
    isExportLoading,
    printRef,
    todayString,
    testToggleChecked,
    openTestTypeDialog,
    testTypeData,
    billsChanged,

    // handlers
    setAnchorEl,
    setSelectedRx,
    setOpenDeleteDialog,
    setBills,
    setRxNumberInput,
    setRxNumberError,
    setOpenSingleRxDialog,
    setNotes,
    setOpenExportDialog,
    setSelectedRxId,
    setOpenCancelTaxierenDialog,
    setTestTypeData,
    setOpenTestTypeDialog,
    setTestToggleChecked,
    setBillsChanged,
    setIsDrucken,

    handleMenuOpen,
    handleMenuClose,
    handleDeleteClick,
    handleDeleteCancel,
    handleDeleteConfirm,
    handleSoftDeleteCancel,
    handleSoftDeleteConfirm,
    handleTaxierenClick,
    handleCancelTaxierenConfirm,
    handleCancelTaxierenClick,
    handleValidationFilter,
    handleSave,
    handleDrucken: effectiveHandleDrucken,
    handleExport,
    handleExportConfirm,
    handleTestToggleChange,
    handleTestTypeDialogClose,
    handleNotesChange,

    // from props
    onRowClick,
    onRxDelete,
    onRemoveAll,
  };
};
